
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import InactiveStateHandler from "@/components/subscription/InactiveStateHandler";
import ErrorBoundary from "@/components/ErrorBoundary";
import LandingPage from "./pages/LandingPage";
// Legacy pages removed: DocumentPage, TopicPage, SessionPage
import LoginPage from "./pages/LoginPage";
import SignUpPage from "./pages/SignUpPage";
import WelcomeVideoPage from "./pages/WelcomeVideoPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import PersonalizationPage from "./pages/PersonalizationPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import AccountSettings from "./pages/AccountSettings";

import ExplorePage from "./pages/ExplorePage";
import DiscoverPage from "./pages/DiscoverPage";
import CommunityTopicDetailPage from "./pages/CommunityTopicDetailPage";
import ChatPage from "./components/chat/ChatPage";
import NotFound from "./pages/NotFound";
import AgentPage from "./pages/AgentPage";
import { useEffect } from "react";
import SuccessPage from "./pages/SuccessPage";
import CancelPage from "./pages/CancelPage";

// Create QueryClient with memoization to prevent HMR issues
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/cancel" element={<CancelPage />} />
              <Route path="/welcome-video" element={<ProtectedRoute><WelcomeVideoPage /></ProtectedRoute>} />
              
              {/* Protected routes with inactive state handling */}
              <Route path="/" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <LandingPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              <Route path="/explore" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <ExplorePage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              <Route path="/community/:id" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <CommunityTopicDetailPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              <Route path="/discover" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <DiscoverPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              <Route path="/topic/:id" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <ErrorBoundary>
                      <ChatPage />
                    </ErrorBoundary>
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              <Route path="/chat/:id" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <ChatPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              <Route path="/agent" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <AgentPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              {/* Onboarding removed (legacy) */}
              <Route path="/personalization" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <PersonalizationPage />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              <Route path="/account" element={
                <ProtectedRoute>
                  <InactiveStateHandler>
                    <AccountSettings />
                  </InactiveStateHandler>
                </ProtectedRoute>
              } />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
